import { useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { reloadToLatest } from '@/lib/chunk-error';

// Cada cuánto sondear /version.json (además de al recuperar foco de pestaña).
const POLL_MS = 60_000;

/**
 * Avisa (sin recargar) cuando hay un deploy nuevo, para que las sesiones
 * abiertas no tengan que refrescar a ciegas. Compara la versión incrustada en
 * este bundle (__BUILD_VER__) con la publicada en /version.json por el último
 * build. NO recarga automáticamente: cortaría trabajo en curso; el usuario
 * decide cuándo con el botón "Actualizar". Sólo avisa una vez por sesión.
 */
export function useVersionCheck() {
  const notified = useRef(false);

  useEffect(() => {
    let cancelled = false;

    async function check() {
      if (notified.current) return;
      try {
        const res = await fetch(`/version.json?ts=${Date.now()}`, {
          cache: 'no-store',
        });
        if (!res.ok) return;
        const data = (await res.json()) as { version?: string };
        if (cancelled || notified.current) return;
        if (data.version && data.version !== __BUILD_VER__) {
          notified.current = true;
          toast('Hay una versión nueva de Pelip', {
            description: 'Actualiza para ver los últimos cambios.',
            duration: Infinity,
            action: {
              label: 'Actualizar',
              // Recarga forzando HTML fresco (salta caché de navegador y edge):
              // un reload normal a veces sirve index.html viejo y se atasca.
              onClick: () => reloadToLatest(),
            },
          });
        }
      } catch {
        // Sin red, JSON inválido (p. ej. el index.html del dev server), etc.:
        // ignorar en silencio, no es un error que deba ver el usuario.
      }
    }

    check();
    const id = window.setInterval(check, POLL_MS);
    const onFocus = () => check();
    window.addEventListener('focus', onFocus);
    return () => {
      cancelled = true;
      window.clearInterval(id);
      window.removeEventListener('focus', onFocus);
    };
  }, []);
}
